/**
 * Layout Store (Zustand)
 *
 * Unified drawer state management — which drawer is open, width/height
 * preferences, pipeline-add mode, and auto-close behavior. Drawers are
 * purely UI chrome; they read from existing stores and don't own data.
 *
 * Consolidates the former useDrawerStore into this single source of truth.
 *
 * @see docs/Reaserch/BackendAdvocacyDesignDoc.md §1
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Which drawer is currently open (only one at a time), or null for all closed. */
export type DrawerType = 'tools' | 'settings' | 'pipeline-details' | null;

/** Per-drawer size preferences. */
export interface DrawerPreferences {
  tools: { width: number };
  settings: { width: number };
  'pipeline-details': { height: number };
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_DRAWER_PREFERENCES: DrawerPreferences = {
  tools: { width: 400 },
  settings: { width: 400 },
  'pipeline-details': { height: 360 },
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy migration: swedevtools-drawer-preferences → swedevtools-layout
// Runs once before store creation. Idempotent — skips if new key exists.
// ─────────────────────────────────────────────────────────────────────────────

const LEGACY_KEY = 'swedevtools-drawer-preferences';
const LAYOUT_KEY = 'swedevtools-layout';

function migrateFromLegacyDrawerStore(): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = localStorage.getItem(LAYOUT_KEY);
    if (existing) return; // new key already present — nothing to import

    const oldRaw = localStorage.getItem(LEGACY_KEY);
    if (!oldRaw) return;

    const oldData = JSON.parse(oldRaw);
    // Zustand persist envelope: { state: { ... }, version: N }
    const oldPrefs = oldData?.state?.preferences ?? {};

    const safeWidth = (v: unknown, fallback: number) =>
      typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;

    const migratedState = {
      state: {
        drawerPreferences: {
          tools: { width: safeWidth(oldPrefs?.tools?.width, 400) },
          settings: { width: safeWidth(oldPrefs?.settings?.width, 400) },
          'pipeline-details': { height: 360 },
        },
      },
      version: 2,
    };

    localStorage.setItem(LAYOUT_KEY, JSON.stringify(migratedState));
    localStorage.removeItem(LEGACY_KEY); // best-effort cleanup
  } catch {
    // Silent — defaults will be used
  }
}

migrateFromLegacyDrawerStore();

// ─────────────────────────────────────────────────────────────────────────────
// Store interface
// ─────────────────────────────────────────────────────────────────────────────

interface LayoutStoreState {
  /** Currently open drawer, or null if all closed. */
  activeDrawer: DrawerType;
  /** When true, the tools drawer was opened from a pipeline "+" button — tools add to pipeline, not create tabs. */
  pipelineAddMode: boolean;
  /** Persisted size preferences per drawer. */
  drawerPreferences: DrawerPreferences;

  /** Open a drawer (closes any currently open one, resets pipelineAddMode). */
  openDrawer: (drawer: NonNullable<DrawerType>) => void;
  /** Open the tools drawer in pipeline-add mode. */
  openDrawerForPipeline: () => void;
  /** Close the currently open drawer (resets pipelineAddMode). */
  closeDrawer: () => void;
  /** Toggle a drawer open/closed (resets pipelineAddMode). */
  toggleDrawer: (drawer: NonNullable<DrawerType>) => void;
  /** Update a drawer's width preference. */
  setDrawerSize: (drawer: 'tools' | 'settings', width: number) => void;
  /** Update the pipeline-details drawer height. */
  setPipelineDetailsHeight: (height: number) => void;
  /** Reset layout to defaults. */
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useLayoutStore = create<LayoutStoreState>()(
  persist(
    (set, get) => ({
      activeDrawer: null,
      pipelineAddMode: false,
      drawerPreferences: { ...DEFAULT_DRAWER_PREFERENCES },

      openDrawer: (drawer) => {
        set({ activeDrawer: drawer, pipelineAddMode: false });
      },

      openDrawerForPipeline: () => {
        set({ activeDrawer: 'tools', pipelineAddMode: true });
      },

      closeDrawer: () => {
        set({ activeDrawer: null, pipelineAddMode: false });
      },

      toggleDrawer: (drawer) => {
        const current = get().activeDrawer;
        set({
          activeDrawer: current === drawer ? null : drawer,
          pipelineAddMode: false,
        });
      },

      setDrawerSize: (drawer, width) => {
        set((state) => ({
          drawerPreferences: {
            ...state.drawerPreferences,
            [drawer]: { width },
          },
        }));
      },

      setPipelineDetailsHeight: (height) => {
        set((state) => ({
          drawerPreferences: {
            ...state.drawerPreferences,
            'pipeline-details': { height },
          },
        }));
      },

      reset: () => {
        set({
          activeDrawer: null,
          pipelineAddMode: false,
          drawerPreferences: { ...DEFAULT_DRAWER_PREFERENCES },
        });
      },
    }),
    {
      name: LAYOUT_KEY,
      version: 2,
      migrate: (persisted, version) => {
        const state = {
          drawerPreferences: { ...DEFAULT_DRAWER_PREFERENCES },
          ...(persisted as Record<string, unknown>),
        } as Record<string, unknown>;

        // v1 → v2: ensure pipeline-details exists and validate numerics
        if (version != null && version < 2) {
          const prefs = state.drawerPreferences as Record<string, Record<string, unknown>> | undefined;
          if (prefs) {
            const safeWidth = (v: unknown, fallback: number) =>
              typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
            const safeHeight = safeWidth; // same validation logic

            prefs.tools = { width: safeWidth(prefs.tools?.width, 400) };
            prefs.settings = { width: safeWidth(prefs.settings?.width, 400) };
            prefs['pipeline-details'] = {
              height: safeHeight(prefs['pipeline-details']?.height, 360),
            };
          } else {
            state.drawerPreferences = { ...DEFAULT_DRAWER_PREFERENCES };
          }
        }

        return state as unknown as LayoutStoreState;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        drawerPreferences: state.drawerPreferences,
      }),
    }
  )
);
